import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    HardDrive, Folder, FolderPlus, Upload, Download, Trash2, FileText,
    Image as ImageIcon, ChevronRight, ChevronDown, Home, MoreVertical, X, Search, Loader2,
    Share2, Mail, MessageSquare, Link as LinkIcon,
    // iter-159 — curated icons for the folder icon picker.
    Truck, Hammer, HardHat, Wrench, Trees, ShieldAlert, Warehouse, Anchor,
    Sparkles, Building2, GripVertical,
    // iter-164 — Trash View icons.
    Trash, RotateCcw,
} from "lucide-react";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { api } from "../lib/api/_client";
import {
    listFiles, listSystemFolders, filesUsage, createFolder, uploadFile,
    renameFile, deleteFile, downloadFileUrl, previewFile, listAllFolders,
    searchFiles, updateFileIcon, reorderSystemFolders,
    uploadFolderIconSvg, clearFolderIconSvg,
    getFolderIconLibrary, applyFolderIconFromLibrary, getFilesStateVersion,
    bulkDeleteFiles, bulkDownloadFiles, bulkRestoreFiles,
    listTrashFiles, bulkPermanentDeleteFiles,
} from "../lib/api/files";
import { useAuth } from "../lib/auth";

// iter-159 — curated icon library for the folder icon picker. Add here to
// expose new options; the string key is stored on the file doc as `icon`.
const FOLDER_ICON_OPTIONS = {
    folder: Folder,
    truck: Truck,
    hammer: Hammer,
    "hard-hat": HardHat,
    wrench: Wrench,
    trees: Trees,
    "shield-alert": ShieldAlert,
    warehouse: Warehouse,
    anchor: Anchor,
    sparkles: Sparkles,
    building: Building2,
};
const resolveFolderIcon = (key) => FOLDER_ICON_OPTIONS[key] || Folder;

// Local util — surface a friendly message from an axios error.
const formatApiError = (err) => (
    err?.response?.data?.detail
    || err?.message
    || "Something went wrong."
);

const fmtBytes = (n) => {
    if (!n) return "0 B";
    const u = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(i ? 1 : 0)} ${u[i]}`;
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch { return "—"; }
};

// iter-148 — pretty ETA string ("42s" / "3m 12s" / "1h 4m").
const fmtEta = (secs) => {
    if (!Number.isFinite(secs) || secs <= 0) return "calculating…";
    const s = Math.ceil(secs);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return `${m}m ${rem}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
};

const iconFor = (item) => {
    if (item.is_folder) return Folder;
    const n = (item.name || "").toLowerCase();
    if (item.mime_type?.startsWith("image/") || n.match(/\.(png|jpe?g|gif|webp|svg)$/)) return ImageIcon;
    return FileText;
};

export default function FilesPage() {
    // eslint-disable-next-line no-unused-vars
    const _auth = useAuth();
    // iter-157 — superadmin gets an extra "Rename" affordance on pinned
    // system folders (backend enforces the same restriction).
    const isSuperadmin = (_auth?.user?.role || "") === "superadmin";
    // iter-149 — folder path is URL-backed via ?folder=<id>, so browser
    // Back/Forward navigates the folder tree (not off the Files page)
    // and drops can safely snapshot the current target from the URL.
    const [searchParams, setSearchParams] = useSearchParams();
    const parent = searchParams.get("folder") || "root";
    // iter-164 — Trash View is a synthetic parent id ("trash") so the
    // URL survives back/forward navigation and the sidebar highlight
    // reuses the existing `parent === X` check.
    const isTrashView = parent === "trash";
    const setParent = (id) => {
        const next = new URLSearchParams(searchParams);
        if (!id || id === "root") next.delete("folder");
        else next.set("folder", id);
        setSearchParams(next);
    };
    const [items, setItems] = useState([]);
    const [breadcrumb, setBreadcrumb] = useState([{ file_id: "root", name: "Home" }]);
    const [systemFolders, setSystemFolders] = useState([]);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    // iter-134b: global search results — populated when the search
    // box has text; scans every folder in scope via /files/search.
    const [searchResults, setSearchResults] = useState(null);
    useEffect(() => {
        if (!search.trim()) { setSearchResults(null); return; }
        const t = setTimeout(async () => {
            try {
                const d = await searchFiles(search.trim());
                setSearchResults(d.items || []);
            } catch { setSearchResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [search]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [preview, setPreview] = useState(null);
    // iter-151 — share menu (SMS / Email / Native / Copy) inside the preview.
    const [shareOpen, setShareOpen] = useState(false);
    useEffect(() => { if (!preview) setShareOpen(false); }, [preview]);
    const [renaming, setRenaming] = useState(null);
    const [renameName, setRenameName] = useState("");
    // iter-150 — right-click context menu on a sidebar tree row.
    const [treeMenu, setTreeMenu] = useState(null); // { x, y, folder }
    // iter-159 — folder being icon-picked; null when the picker is closed.
    const [iconPicker, setIconPicker] = useState(null);
    // iter-161 — persistent library of previously-uploaded SVGs.
    const [iconLibrary, setIconLibrary] = useState([]);
    // iter-162 — multi-select for bulk delete + download preserving tree.
    const [selected, setSelected] = useState(() => new Set());
    // iter-163 — anchor index for shift-click range selection.
    const lastSelectedIdxRef = useRef(null);
    const toggleSelected = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    // Clear selection whenever the folder changes.
    useEffect(() => { setSelected(new Set()); }, [parent]);

    useEffect(() => {
        if (!iconPicker || !isSuperadmin) return;
        (async () => {
            try {
                const d = await getFolderIconLibrary();
                setIconLibrary(d.items || []);
            } catch { /* non-fatal */ }
        })();
    }, [iconPicker, isSuperadmin]);
    useEffect(() => {
        if (!treeMenu) return;
        const onKey = (e) => { if (e.key === "Escape") setTreeMenu(null); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [treeMenu]);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(null);
    const [folderTree, setFolderTree] = useState([]);
    // iter-149 — Explorer-style collapse/expand. Set of folder ids that
    // are currently expanded in the sidebar tree.
    const [expandedIds, setExpandedIds] = useState(new Set());
    const toggleExpanded = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const [viewMode, setViewMode] = useState("list"); // iter-134
    const fileInputRef = useRef(null);
    // iter-150 — abort controller so the Cancel button can kill the
    // in-flight xhr AND stop the queue of remaining files/folders.
    const uploadAbortRef = useRef(null);
    // iter-158 — pause/resume gate. Between files we `await` the current
    // resolver; toggling pause off resolves it and the loop marches on.
    const pauseRef = useRef({ paused: false, resolver: null });
    const waitForResume = () => (
        pauseRef.current.paused
            ? new Promise((res) => { pauseRef.current.resolver = res; })
            : Promise.resolve()
    );
    const togglePause = () => {
        pauseRef.current.paused = !pauseRef.current.paused;
        setUploading((prev) => prev ? { ...prev, paused: pauseRef.current.paused } : prev);
        if (!pauseRef.current.paused && pauseRef.current.resolver) {
            pauseRef.current.resolver();
            pauseRef.current.resolver = null;
        }
    };
    // iter-158 — files that errored during the last run, ready for
    // one-click retry via a chip inside the same dialog.
    const failedQueueRef = useRef([]);

    // iter-133: cache image thumbnails so <img src=...> URLs are stable
    // and each blob loads exactly once per session. Object URLs are
    // revoked on unmount to prevent memory leaks.
    const thumbCache = useRef(new Map());
    const isImage = (item) => {
        if (!item || item.is_folder) return false;
        const mime = (item.mime_type || "").toLowerCase();
        const name = (item.name || "").toLowerCase();
        // iter-167 — HEIC/HEIF now count as images; the backend
        // ?render=web query transcodes them to JPEG on the fly so the
        // browser can actually display them.
        return mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|heic|heif)$/i.test(name);
    };
    const thumbSrc = (item) => downloadFileUrl(item.file_id);

    useEffect(() => {
        (async () => {
            try {
                const d = await listAllFolders();
                setFolderTree(d.items || []);
            } catch { /* non-fatal */ }
        })();
    }, [items]);

    // iter-149 — auto-expand every ancestor of the current folder so it's
    // visible in the sidebar tree after navigation.
    useEffect(() => {
        if (parent === "root" || folderTree.length === 0) return;
        setExpandedIds((prev) => {
            const next = new Set(prev);
            let cur = parent;
            const seen = new Set();
            while (cur && cur !== "root" && !seen.has(cur)) {
                next.add(cur);
                seen.add(cur);
                const p = folderTree.find((x) => x.file_id === cur);
                cur = p ? p.parent_id : null;
            }
            return next;
        });
    }, [parent, folderTree]);

    // iter-149 — parent → children[] lookup, sorted alphabetically.
    const childrenByParent = useMemo(() => {
        const m = new Map();
        folderTree.forEach((f) => {
            const p = f.parent_id || "root";
            if (!m.has(p)) m.set(p, []);
            m.get(p).push(f);
        });
        m.forEach((arr) => arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        return m;
    }, [folderTree]);

    // iter-149 — flatten only the *visible* rows (root children + expanded
    // descendants) into a single array so render is non-recursive. Keeps
    // Fast Refresh happy and traversal cheap.
    const visibleRows = useMemo(() => {
        const out = [];
        const stack = [...(childrenByParent.get("root") || []).map((f) => ({ f, depth: 0 }))].reverse();
        while (stack.length) {
            const { f, depth } = stack.pop();
            out.push({ folder: f, depth });
            if (expandedIds.has(f.file_id)) {
                const kids = childrenByParent.get(f.file_id) || [];
                for (let i = kids.length - 1; i >= 0; i--) {
                    stack.push({ f: kids[i], depth: depth + 1 });
                }
            }
        }
        return out;
    }, [childrenByParent, expandedIds]);

    const refresh = async () => {
        setLoading(true);
        try {
            const [ls, sys, u] = await Promise.all([
                // iter-164 — Trash View pulls soft-deleted docs from a
                // dedicated endpoint so list_files' default filter still
                // applies everywhere else.
                isTrashView ? listTrashFiles() : listFiles(parent),
                parent === "root" ? listSystemFolders() : Promise.resolve({ items: [] }),
                filesUsage(),
            ]);
            setItems(ls.items || []);
            setBreadcrumb(ls.breadcrumb || [{ file_id: "root", name: "Home" }]);
            setSystemFolders(sys.items || []);
            setUsage(u);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [parent]);

    // iter-161 — poll a cheap version counter so other laptops see icon /
    // rename / reorder changes without a hard reload. We only refresh
    // when the number actually changes; the endpoint is O(1) in-memory.
    useEffect(() => {
        let cancelled = false;
        let lastVersion = -1;
        const tick = async () => {
            try {
                const { version } = await getFilesStateVersion();
                if (cancelled) return;
                if (lastVersion === -1) { lastVersion = version; return; }
                if (version !== lastVersion) {
                    lastVersion = version;
                    refresh();
                }
            } catch { /* silent */ }
        };
        const id = setInterval(tick, 5000);
        tick();
        return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // iter-158 — extracted shared upload loop (used by both the
    // file-picker flow and the folder-drop Phase 2). Supports pause,
    // abort, per-file progress and returns the errored files so the
    // retry pill can rerun just the failures.
    const runUploadBatch = async ({
        queue,           // [{ file, parentId }]
        controller,
        targetName,
        mode,
        totalBytes,
        startTime,
        totalFiles,
        initialCompleted = 0,
    }) => {
        let completedBytes = initialCompleted;
        let doneCount = 0;
        const errors = [];
        for (let i = 0; i < queue.length; i++) {
            if (controller.signal.aborted) break;
            await waitForResume();
            if (controller.signal.aborted) break;
            const { file, parentId } = queue[i];
            try {
                await uploadFile(file, parentId, (e) => {
                    const currentLoaded = e.loaded || 0;
                    setUploading((prev) => prev ? {
                        ...prev,
                        currentIdx: i,
                        currentName: file.name,
                        currentSize: file.size || 0,
                        currentLoaded,
                        loadedBytes: completedBytes + currentLoaded,
                    } : prev);
                }, controller.signal);
                completedBytes += (file.size || 0);
                doneCount += 1;
                setUploading((prev) => prev ? {
                    ...prev,
                    doneFiles: doneCount + (initialCompleted ? 0 : 0),
                    loadedBytes: completedBytes,
                } : prev);
            } catch (err) {
                if (controller.signal.aborted || err?.code === "ERR_CANCELED" || err?.name === "CanceledError") break;
                const reason = err?.response?.data?.detail || err?.message || "Upload failed";
                errors.push({ file, parentId, reason });
                completedBytes += (file.size || 0);
                setUploading((prev) => prev ? { ...prev, loadedBytes: completedBytes } : prev);
            }
        }
        return { doneCount, errors, canceled: controller.signal.aborted };
    };

    const compressionOk = (bytes) => (
        bytes < 500 * 1024 * 1024
        // eslint-disable-next-line no-alert
        || window.confirm(`This upload is ${fmtBytes(bytes)}. Continue?`)
    );

    const finishUploadRun = ({ mode, targetName, doneCount, totalFiles, errors, canceled }) => {
        uploadAbortRef.current = null;
        pauseRef.current = { paused: false, resolver: null };
        if (errors.length > 0) {
            // iter-158 — keep the dialog open in "finished" state with a
            // Retry Failed chip.
            failedQueueRef.current = errors;
            setUploading((prev) => prev ? {
                ...prev,
                finished: true,
                paused: false,
                errors,
            } : prev);
            toast.warning(`Uploaded ${doneCount} of ${totalFiles} — ${errors.length} failed. Retry from the dialog.`);
        } else {
            setUploading(null);
            if (canceled) toast.warning(`Canceled — ${doneCount} of ${totalFiles} uploaded to ${targetName}`);
            else toast.success(`Uploaded ${doneCount} of ${totalFiles} to ${targetName}`);
        }
        refresh();
    };

    const retryFailed = async () => {
        const queue = failedQueueRef.current || [];
        if (queue.length === 0) return;
        failedQueueRef.current = [];
        const controller = new AbortController();
        uploadAbortRef.current = controller;
        const totalBytes = queue.reduce((s, q) => s + (q.file.size || 0), 0);
        setUploading((prev) => prev ? {
            ...prev,
            finished: false,
            paused: false,
            errors: [],
            totalFiles: queue.length,
            doneFiles: 0,
            currentIdx: 0,
            currentName: queue[0]?.file?.name || "",
            currentSize: queue[0]?.file?.size || 0,
            currentLoaded: 0,
            totalBytes,
            loadedBytes: 0,
            startTime: Date.now(),
        } : prev);
        const { doneCount, errors, canceled } = await runUploadBatch({
            queue,
            controller,
            targetName: uploading?.targetName || "target",
            mode: uploading?.mode || "files",
            totalBytes,
            startTime: Date.now(),
            totalFiles: queue.length,
        });
        finishUploadRun({
            mode: uploading?.mode || "files",
            targetName: uploading?.targetName || "target",
            doneCount,
            totalFiles: queue.length,
            errors,
            canceled,
        });
    };

    const handleUpload = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const targetParent = parent;
        const targetName = (breadcrumb[breadcrumb.length - 1]?.name) || "Home";
        const totalBytes = files.reduce((s, f) => s + (f.size || 0), 0);
        if (!compressionOk(totalBytes)) return;
        const controller = new AbortController();
        uploadAbortRef.current = controller;
        setUploading({
            mode: "files",
            targetName,
            totalFiles: files.length,
            doneFiles: 0,
            currentIdx: 0,
            currentName: files[0]?.name || "",
            currentSize: files[0]?.size || 0,
            currentLoaded: 0,
            totalBytes,
            loadedBytes: 0,
            startTime: Date.now(),
            canceled: false,
            paused: false,
            errors: [],
            finished: false,
        });
        const queue = files.map((file) => ({ file, parentId: targetParent }));
        const { doneCount, errors, canceled } = await runUploadBatch({
            queue,
            controller,
            targetName,
            mode: "files",
            totalBytes,
            startTime: Date.now(),
            totalFiles: files.length,
        });
        finishUploadRun({
            mode: "files",
            targetName,
            doneCount,
            totalFiles: files.length,
            errors,
            canceled,
        });
    };

    const handleCreateFolder = async () => {
        // eslint-disable-next-line no-alert
        const name = window.prompt("Folder name?");
        if (!name) return;
        try {
            await createFolder(name, parent);
            refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const handleDelete = async (id) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm("Delete this item and everything inside?")) return;
        try { await deleteFile(id); refresh(); } catch (err) { toast.error(formatApiError(err)); }
    };

    const handleRename = async () => {
        if (!renaming || !renameName.trim()) { setRenaming(null); return; }
        try {
            await renameFile(renaming.file_id, renameName.trim());
            setRenaming(null);
            refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openItem = async (item) => {
        // iter-164 — In trash view rows are inert (bulk bar handles the
        // Restore / Delete-forever actions).
        if (isTrashView) return;
        if (item.is_folder) { setParent(item.file_id); return; }
        try {
            const info = await previewFile(item.file_id);
            setPreview({ item, info });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);
        // iter-149 — snapshot the destination folder now, before any
        // React re-renders or async awaits can shift it. Prevents the
        // "I dropped into Demolition, everything landed at root" bug.
        const targetParent = parent;
        const targetName = (breadcrumb[breadcrumb.length - 1]?.name) || "Home";
        // iter-134: whole-folder drag-drop support. Snapshot everything
        // synchronously — DataTransferItemList is invalidated after the
        // first await.
        const items = Array.from(e.dataTransfer?.items || []);
        const entries = items
            .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
            .filter(Boolean);
        if (entries.length === 0) {
            if (e.dataTransfer?.files?.length) handleUpload(e.dataTransfer.files);
            return;
        }
        // iter-150 — abortable folder walk.
        const controller = new AbortController();
        uploadAbortRef.current = controller;

        // iter-156 — TWO-PHASE folder upload so we can show the same
        // %/ETA/bytes progress as the file-picker flow (Windows Explorer
        // style). Phase 1: recursively read the directory tree, creating
        // matching folders server-side and collecting a flat list of
        // {file, parentId}. Phase 2: upload each file with per-file
        // progress, tracking total bytes for a real percentage + ETA.
        setUploading({
            mode: "folder",
            targetName,
            phase: "scan",
            totalFiles: 0,
            doneFiles: 0,
            currentIdx: 0,
            currentName: "Scanning folder tree…",
            currentSize: 0,
            currentLoaded: 0,
            totalBytes: 0,
            loadedBytes: 0,
            startTime: Date.now(),
        });

        // -------- Phase 1: scan + mkdir --------
        const flat = [];
        const collect = async (entry, parentId) => {
            if (controller.signal.aborted) return;
            if (entry.isFile) {
                const f = await new Promise((res) => entry.file(res, () => res(null)));
                if (f) {
                    flat.push({ file: f, parentId });
                    setUploading((prev) => prev ? {
                        ...prev,
                        totalFiles: flat.length,
                        totalBytes: prev.totalBytes + (f.size || 0),
                        currentName: `Scanning: ${f.name}`,
                    } : prev);
                }
                return;
            }
            if (entry.isDirectory) {
                let created;
                try {
                    created = await createFolder(entry.name, parentId);
                } catch (err) {
                    toast.error(`Folder ${entry.name}: ${formatApiError(err)}`);
                    return;
                }
                // readEntries() only returns a batch at a time — loop until empty.
                const reader = entry.createReader();
                while (true) {
                    if (controller.signal.aborted) return;
                    const batch = await new Promise((res) => reader.readEntries((r) => res(r), () => res([])));
                    if (!batch || batch.length === 0) break;
                    for (const child of batch) {
                        if (controller.signal.aborted) return;
                        await collect(child, created.file_id);
                    }
                }
            }
        };
        for (const entry of entries) {
            if (controller.signal.aborted) break;
            await collect(entry, targetParent);
        }

        // iter-158 — before Phase 2, warn if the scanned folder is huge.
        if (!compressionOk(flat.reduce((s, q) => s + (q.file.size || 0), 0))) {
            setUploading(null);
            uploadAbortRef.current = null;
            return;
        }

        // -------- Phase 2: upload with real progress --------
        // Reset the clock so the ETA math is based on upload speed
        // only (not the time spent scanning the tree).
        setUploading((prev) => prev ? {
            ...prev,
            phase: "upload",
            startTime: Date.now(),
            currentName: flat[0]?.file?.name || "",
            currentSize: flat[0]?.file?.size || 0,
            paused: false,
            errors: [],
            finished: false,
        } : prev);

        const totalBytesUpload = flat.reduce((s, q) => s + (q.file.size || 0), 0);
        const { doneCount, errors, canceled } = await runUploadBatch({
            queue: flat,
            controller,
            targetName,
            mode: "folder",
            totalBytes: totalBytesUpload,
            startTime: Date.now(),
            totalFiles: flat.length,
        });
        finishUploadRun({
            mode: "folder",
            targetName,
            doneCount,
            totalFiles: flat.length,
            errors,
            canceled,
        });
    };

    const filtered = searchResults !== null
        ? searchResults
        : items.filter((i) => !search || (i.name || "").toLowerCase().includes(search.toLowerCase()));

    // iter-164 — Ctrl-A / Cmd-A selects every eligible row in the
    // current listing. Ignored while typing in inputs, textareas or
    // during upload/rename/preview modals so we don't stomp on the
    // native input Select All.
    // NOTE: must be declared AFTER `filtered` — referencing it in the
    // dependency array before its const initialisation throws a TDZ
    // ReferenceError and blanks the whole page.
    useEffect(() => {
        const onKey = (e) => {
            const isA = (e.key === "a" || e.key === "A");
            if (!isA || !(e.ctrlKey || e.metaKey)) return;
            const t = e.target;
            const tag = (t?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
            if (renaming || preview || uploading || iconPicker) return;
            e.preventDefault();
            const next = new Set(selected);
            const eligible = filtered.filter((f) => !f.system);
            const alreadyAll = eligible.length > 0 && eligible.every((f) => next.has(f.file_id));
            if (alreadyAll) {
                eligible.forEach((f) => next.delete(f.file_id));
            } else {
                eligible.forEach((f) => next.add(f.file_id));
            }
            setSelected(next);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtered, selected, renaming, preview, uploading, iconPicker]);

    const pct = usage?.quota_bytes ? Math.min(100, (usage.used_bytes / usage.quota_bytes) * 100) : 0;

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-slate-50" data-testid="files-page">
            <TopNav />
            <main className="flex-1 min-h-0 overflow-y-auto">
                <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 pb-40 md:flex md:gap-4">
                    {/* iter-134 — Explorer-style folder tree sidebar */}
                    <aside className="hidden md:block w-52 shrink-0" data-testid="files-tree">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 mb-2">
                            Folders
                        </div>
                        <div className="rounded border border-slate-200 bg-white shadow-sm p-1 max-h-[60vh] overflow-y-auto text-sm">
                            <button
                                onClick={() => setParent("root")}
                                className={`w-full text-left px-2 py-1 rounded flex items-center gap-1 ${parent === "root" ? "bg-[#00A3FF]/10 text-[#00A3FF]" : "hover:bg-slate-100 text-slate-700"}`}
                                data-testid="files-tree-root"
                            >
                                <Home className="w-3.5 h-3.5" /> Home
                            </button>
                            {visibleRows.map(({ folder, depth }) => {
                                const kids = childrenByParent.get(folder.file_id) || [];
                                const hasChildren = kids.length > 0;
                                const expanded = expandedIds.has(folder.file_id);
                                const active = parent === folder.file_id;
                                return (
                                    <div
                                        key={folder.file_id}
                                        className="flex items-stretch"
                                        style={{ paddingLeft: `${depth * 12}px` }}
                                    >
                                        {hasChildren ? (
                                            <button
                                                onClick={() => toggleExpanded(folder.file_id)}
                                                className="w-5 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500 shrink-0"
                                                data-testid={`files-tree-toggle-${folder.file_id}`}
                                                aria-label={expanded ? "Collapse" : "Expand"}
                                            >
                                                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                            </button>
                                        ) : (
                                            <span className="w-5 shrink-0" />
                                        )}
                                        <button
                                            onClick={() => setParent(folder.file_id)}
                                            onDoubleClick={() => hasChildren && toggleExpanded(folder.file_id)}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                setTreeMenu({
                                                    x: Math.min(e.clientX, window.innerWidth - 200),
                                                    y: Math.min(e.clientY, window.innerHeight - 160),
                                                    folder,
                                                });
                                            }}
                                            className={`flex-1 min-w-0 text-left py-1 pr-2 rounded flex items-center gap-1 truncate ${active ? "bg-[#00A3FF]/10 text-[#00A3FF]" : "hover:bg-slate-100 text-slate-700"}`}
                                            data-testid={`files-tree-${folder.file_id}`}
                                        >
                                            <Folder className={`w-3.5 h-3.5 shrink-0 ${folder.system ? "text-[#00A3FF]" : "text-[#FFA726]"}`} />
                                            <span className="truncate">{folder.name}</span>
                                        </button>
                                    </div>
                                );
                            })}
                            {/* iter-164 — Recently deleted (Trash View) entry. */}
                            <div className="border-t border-slate-200 my-1" />
                            <button
                                onClick={() => setParent("trash")}
                                className={`w-full text-left px-2 py-1 rounded flex items-center gap-1 ${isTrashView ? "bg-rose-100 text-rose-700" : "hover:bg-slate-100 text-slate-700"}`}
                                data-testid="files-tree-trash"
                            >
                                <Trash className="w-3.5 h-3.5" /> Recently deleted
                            </button>
                        </div>
                        {/* iter-143 — capacity meter now reflects the real
                            VPS filesystem: bar width tracks server-wide
                            disk usage and the copy shows both what the
                            user has uploaded AND how much free space the
                            server has left. */}
                        {usage && (() => {
                            const total = usage.disk_total_bytes || usage.quota_bytes || 1;
                            const diskUsed = usage.disk_used_bytes ?? Math.max(total - (usage.disk_free_bytes ?? 0), 0);
                            const diskPct = Math.min(100, (diskUsed / total) * 100);
                            return (
                                <div className="mt-3 rounded border border-slate-200 bg-white shadow-sm p-3" data-testid="files-capacity-bar">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <HardDrive className="w-3.5 h-3.5 text-[#00A3FF]" />
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Server capacity</span>
                                    </div>
                                    <div className="h-2 bg-slate-200 rounded overflow-hidden">
                                        <div
                                            className={`h-full transition-all ${diskPct > 90 ? "bg-rose-500" : diskPct > 75 ? "bg-amber-400" : "bg-[#00A3FF]"}`}
                                            style={{ width: `${diskPct}%` }}
                                        />
                                    </div>
                                    <div className="mt-1.5 text-[11px] text-slate-700 space-y-0.5">
                                                        <div className="font-bold text-emerald-600">
                                            {fmtBytes(usage.disk_free_bytes ?? Math.max(total - diskUsed, 0))} free
                                        </div>
                                        <div className="text-slate-600">
                                            {fmtBytes(diskUsed)} used of {fmtBytes(total)} · {diskPct.toFixed(1)}%
                                        </div>
                                        <div className="text-slate-600 pt-1 border-t border-slate-200 mt-1">
                                            Your files: <span className="text-slate-900 font-bold">{fmtBytes(usage.used_bytes)}</span> ({usage.file_count} file{usage.file_count === 1 ? "" : "s"})
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </aside>
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <HardDrive className="w-6 h-6 text-[#00A3FF]" />
                            <h1 className="text-2xl font-bold text-slate-900">Files</h1>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="inline-flex rounded overflow-hidden border border-slate-200">
                                <button
                                    onClick={() => setViewMode("list")}
                                    data-testid="files-view-list"
                                    className={`px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${viewMode === "list" ? "bg-[#00A3FF] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                                >List</button>
                                <button
                                    onClick={() => setViewMode("grid")}
                                    data-testid="files-view-grid"
                                    className={`px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${viewMode === "grid" ? "bg-[#00A3FF] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                                >Grid</button>
                            </div>
                            {!isTrashView && (
                                <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCreateFolder}
                                    data-testid="files-new-folder-btn"
                                    className="border-slate-200 text-slate-900 hover:bg-slate-100"
                                >
                                    <FolderPlus className="w-4 h-4 mr-1" /> New folder
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="files-upload-btn"
                                    className="bg-[#007AFF] hover:bg-[#0369A1] text-white"
                                >
                                    <Upload className="w-4 h-4 mr-1" /> Upload
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => handleUpload(e.target.files)}
                                    data-testid="files-file-input"
                                />
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-1 mb-3 flex-wrap text-sm" data-testid="files-breadcrumb">
                        {breadcrumb.map((c, idx) => (
                            <React.Fragment key={c.file_id}>
                                {idx > 0 && <ChevronRight className="w-3 h-3 text-slate-500" />}
                                <button
                                    onClick={() => setParent(c.file_id)}
                                    className="px-2 py-1 rounded hover:bg-slate-100 text-slate-700 hover:text-slate-900 transition-colors"
                                    data-testid={`files-crumb-${c.file_id}`}
                                >
                                    {idx === 0 ? <Home className="w-3.5 h-3.5 inline mr-1" /> : null}
                                    {c.name}
                                </button>
                            </React.Fragment>
                        ))}
                    </div>

                    {!isTrashView && (
                    <div className="relative mb-3">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search all folders…"
                            className="pl-10 bg-white border-slate-300 text-slate-900"
                            data-testid="files-search-input"
                        />
                        {searchResults !== null && (
                            <div className="mt-1 text-[11px] text-slate-600" data-testid="files-search-scope-note">
                                {searchResults.length === 0
                                    ? `No matches for "${search}" anywhere.`
                                    : `${searchResults.length} match${searchResults.length === 1 ? "" : "es"} across all folders. Each row shows its full path.`}
                            </div>
                        )}
                    </div>
                    )}

                    {parent === "root" && !isTrashView && systemFolders.length > 0 && (
                        <div className="mb-4">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 mb-2">
                                System folders
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="files-system-tiles">
                                {systemFolders.map((f, idx) => {
                                    const Icon = resolveFolderIcon(f.icon);
                                    const hasCustom = !!f.custom_icon;
                                    return (
                                    <button
                                        key={f.file_id}
                                        onClick={() => setParent(f.file_id)}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            setTreeMenu({
                                                x: Math.min(e.clientX, window.innerWidth - 220),
                                                y: Math.min(e.clientY, window.innerHeight - 200),
                                                folder: f,
                                            });
                                        }}
                                        data-testid={`files-system-${f.file_id}`}
                                        draggable={isSuperadmin}
                                        onDragStart={(e) => {
                                            if (!isSuperadmin) return;
                                            e.dataTransfer.setData("text/x-system-folder", f.file_id);
                                            e.dataTransfer.effectAllowed = "move";
                                        }}
                                        onDragOver={(e) => {
                                            if (!isSuperadmin) return;
                                            if (!e.dataTransfer.types.includes("text/x-system-folder")) return;
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = "move";
                                        }}
                                        onDrop={async (e) => {
                                            if (!isSuperadmin) return;
                                            const draggedId = e.dataTransfer.getData("text/x-system-folder");
                                            if (!draggedId || draggedId === f.file_id) return;
                                            e.preventDefault();
                                            const next = [...systemFolders];
                                            const from = next.findIndex((x) => x.file_id === draggedId);
                                            if (from < 0) return;
                                            const [moved] = next.splice(from, 1);
                                            next.splice(idx, 0, moved);
                                            setSystemFolders(next);
                                            try {
                                                await reorderSystemFolders(next.map((x) => x.file_id));
                                                toast.success("Pinned folder order saved.");
                                            } catch (err) {
                                                toast.error(formatApiError(err));
                                                refresh();
                                            }
                                        }}
                                        className="relative flex flex-col items-center justify-center gap-1 p-3 rounded border border-slate-200 bg-white shadow-sm hover:border-[#00A3FF] hover:shadow transition-all"
                                    >
                                        {isSuperadmin && (
                                            <GripVertical className="w-3 h-3 text-slate-300 absolute top-1 left-1" />
                                        )}
                                        {hasCustom ? (
                                            <img
                                                src={f.custom_icon}
                                                alt=""
                                                className="w-8 h-8 object-contain"
                                                data-testid={`files-tile-svg-${f.file_id}`}
                                            />
                                        ) : (
                                            <Icon className="w-8 h-8 text-[#00A3FF]" />
                                        )}
                                        <span className="text-xs text-slate-900 text-center leading-tight">{f.name}</span>
                                    </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {isTrashView && (
                        <div className="mb-3 rounded border border-rose-200 bg-rose-50 p-3 flex items-start gap-2" data-testid="files-trash-banner">
                            <Trash className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                            <div className="text-xs text-rose-900">
                                <div className="font-bold uppercase tracking-wider text-rose-700">Recently deleted</div>
                                <div className="mt-0.5">Select items below to <span className="font-bold">Restore</span> them or <span className="font-bold">Delete forever</span>. Restoring puts a file back where it was.</div>
                            </div>
                        </div>
                    )}

                    <div
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        className={`rounded border ${dragOver ? "border-[#00A3FF] bg-[#00A3FF]/5" : "border-slate-200"} bg-white shadow-sm min-h-[300px]`}
                        data-testid="files-drop-zone"
                    >
                        {loading ? (
                            <div className="p-8 text-center text-slate-600">Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div className="p-12 text-center text-slate-600">
                                {isTrashView
                                    ? "Nothing in Recently deleted."
                                    : (search ? "No matches." : "Empty folder — drop files here or click Upload.")}
                            </div>
                        ) : viewMode === "grid" ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 p-2" data-testid="files-grid">
                                {filtered.map((item) => {
                                    const Icon = iconFor(item);
                                    return (
                                        <button
                                            key={item.file_id}
                                            onClick={() => openItem(item)}
                                            className="flex flex-col items-center gap-1 p-3 rounded border border-transparent hover:border-[#00A3FF] hover:bg-slate-50 transition-colors"
                                            data-testid={`files-grid-${item.file_id}`}
                                        >
                                            {isImage(item) ? (
                                                <AuthImage
                                                    fileId={item.file_id}
                                                    alt={item.name}
                                                    className="w-20 h-20 rounded object-cover border border-slate-200"
                                                />
                                            ) : (
                                                <div className="w-20 h-20 flex items-center justify-center rounded bg-slate-100 border border-slate-200">
                                                    <Icon className={`w-10 h-10 ${item.is_folder ? "text-[#FFA726]" : "text-slate-500"}`} />
                                                </div>
                                            )}
                                            <div className="text-xs text-slate-900 text-center truncate w-full">{item.name}</div>
                                            {!item.is_folder && <div className="text-[10px] text-slate-500">{fmtBytes(item.size)}</div>}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-200">
                                {filtered.length > 0 && (
                                    <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200" data-testid="files-select-all-row">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 accent-[#00A3FF]"
                                            checked={filtered.length > 0 && filtered.every((f) => selected.has(f.file_id))}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    const next = new Set(selected);
                                                    filtered.forEach((f) => next.add(f.file_id));
                                                    setSelected(next);
                                                } else {
                                                    const next = new Set(selected);
                                                    filtered.forEach((f) => next.delete(f.file_id));
                                                    setSelected(next);
                                                }
                                            }}
                                            data-testid="files-select-all"
                                        />
                                        <span className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
                                            Select all ({filtered.length})
                                        </span>
                                    </div>
                                )}
                                {filtered.map((item) => {
                                    const Icon = iconFor(item);
                                    const isSelected = selected.has(item.file_id);
                                    return (
                                        <div
                                            key={item.file_id}
                                            className={`flex items-center gap-3 px-3 py-2 transition-colors group ${isSelected ? "bg-[#00A3FF]/10" : "hover:bg-slate-50"}`}
                                            data-testid={`files-row-${item.file_id}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 accent-[#00A3FF] shrink-0"
                                                checked={isSelected}
                                                onChange={() => toggleSelected(item.file_id)}
                                                onClick={(e) => {
                                                    // iter-163 — shift-click selects the range between
                                                    // the last-clicked checkbox and this one.
                                                    e.stopPropagation();
                                                    const idx = filtered.findIndex((f) => f.file_id === item.file_id);
                                                    if (e.shiftKey && lastSelectedIdxRef.current != null) {
                                                        e.preventDefault();
                                                        const from = Math.min(lastSelectedIdxRef.current, idx);
                                                        const to = Math.max(lastSelectedIdxRef.current, idx);
                                                        const next = new Set(selected);
                                                        for (let i = from; i <= to; i++) {
                                                            const row = filtered[i];
                                                            if (row && !row.system) next.add(row.file_id);
                                                        }
                                                        setSelected(next);
                                                    }
                                                    lastSelectedIdxRef.current = idx;
                                                }}
                                                disabled={item.system}
                                                title={item.system ? "System folders can't be selected" : "Click; Shift+click for range"}
                                                data-testid={`files-checkbox-${item.file_id}`}
                                            />
                                            <button
                                                onClick={() => openItem(item)}
                                                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                                            >
                                                {isImage(item) ? (
                                                    <AuthImage
                                                        fileId={item.file_id}
                                                        alt={item.name}
                                                        className="w-8 h-8 rounded object-cover shrink-0 border border-slate-200"
                                                    />
                                                ) : (
                                                    <Icon className={`w-5 h-5 shrink-0 ${item.is_folder ? "text-[#FFA726]" : "text-slate-500"}`} />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-slate-900 truncate">{item.name}</div>
                                                    {!item.is_folder && (
                                                        <div className="text-[11px] text-slate-500">
                                                            {fmtBytes(item.size)} · {fmtDate(item.updated_at || item.created_at)}
                                                            {item.deleted_at && <span className="ml-1 text-rose-600">· Deleted {fmtDate(item.deleted_at)}</span>}
                                                            {item.path && <span className="ml-1 text-slate-600">· {item.path}</span>}
                                                        </div>
                                                    )}
                                                    {item.is_folder && item.deleted_at && (
                                                        <div className="text-[11px] text-rose-600">Deleted {fmtDate(item.deleted_at)}</div>
                                                    )}
                                                    {item.is_folder && item.path && (
                                                        <div className="text-[11px] text-slate-500">{item.path}</div>
                                                    )}
                                                </div>
                                            </button>
                                            {!item.system && !isTrashView && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity"
                                                            data-testid={`files-menu-${item.file_id}`}
                                                        >
                                                            <MoreVertical className="w-4 h-4" />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-white border-slate-200">
                                                        {!item.is_folder && (
                                                            <DropdownMenuItem
                                                                onClick={() => window.open(downloadFileUrl(item.file_id), "_blank")}
                                                                className="cursor-pointer text-slate-900 focus:bg-slate-100"
                                                                data-testid={`files-download-${item.file_id}`}
                                                            >
                                                                <Download className="w-4 h-4 mr-2" /> Download
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuItem
                                                            onClick={() => { setRenaming(item); setRenameName(item.name); }}
                                                            className="cursor-pointer text-slate-900 focus:bg-slate-100"
                                                            data-testid={`files-rename-${item.file_id}`}
                                                        >
                                                            <FileText className="w-4 h-4 mr-2" /> Rename
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={() => handleDelete(item.file_id)}
                                                            className="cursor-pointer text-rose-600 focus:bg-slate-100"
                                                            data-testid={`files-delete-${item.file_id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {usage && (
                        <div className="mt-4 flex items-center gap-3">
                            <div className="flex-1 h-2 bg-slate-200 rounded overflow-hidden">
                                <div
                                    className="h-full bg-[#00A3FF] transition-all"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <div className="text-xs text-slate-600" data-testid="files-usage-label">
                                {fmtBytes(usage.used_bytes)} of {fmtBytes(usage.quota_bytes)} · {usage.file_count} file{usage.file_count === 1 ? "" : "s"}
                            </div>
                        </div>
                    )}
                    </div>{/* end .flex-1 content column */}
                </div>
            </main>

            {uploading && (() => {
                const elapsed = Math.max((Date.now() - uploading.startTime) / 1000, 0.001);
                const knownBytes = uploading.totalBytes > 0;
                const overallPct = knownBytes
                    ? Math.min(100, (uploading.loadedBytes / uploading.totalBytes) * 100)
                    : 0;
                const speed = knownBytes ? (uploading.loadedBytes / elapsed) : 0; // bytes/sec
                const eta = knownBytes && speed > 0
                    ? Math.max(0, (uploading.totalBytes - uploading.loadedBytes) / speed)
                    : Infinity;
                const currentPct = uploading.currentSize
                    ? Math.min(100, (uploading.currentLoaded / uploading.currentSize) * 100)
                    : 0;
                return (
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        data-testid="files-upload-dialog"
                    >
                        <div className="bg-white border border-slate-200 rounded-lg shadow-2xl max-w-md w-full p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-9 h-9 rounded-full bg-[#00A3FF]/10 flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-[#00A3FF]" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-bold text-slate-900 text-sm" data-testid="upload-dialog-title">
                                        {uploading.mode === "folder"
                                            ? (uploading.phase === "scan" ? "Scanning folder" : "Uploading folder")
                                            : "Uploading files"}
                                        {uploading.targetName && (
                                            <span className="text-slate-500 font-normal"> → {uploading.targetName}</span>
                                        )}
                                    </h3>
                                    <div className="text-[11px] text-slate-500">
                                        {uploading.mode === "folder" && uploading.phase === "scan"
                                            ? `Found ${uploading.totalFiles} file${uploading.totalFiles === 1 ? "" : "s"} · ${fmtBytes(uploading.totalBytes)}`
                                            : `${Math.min(uploading.doneFiles + (uploading.currentLoaded > 0 ? 1 : 0), uploading.totalFiles)} of ${uploading.totalFiles} files`}
                                    </div>
                                </div>
                            </div>

                            {knownBytes && (
                                <>
                                    <div className="flex items-baseline justify-between mb-1">
                                        <span className="text-slate-700 text-xs font-medium">Overall progress</span>
                                        <span className="text-slate-900 font-bold text-lg tabular-nums" data-testid="upload-overall-pct">
                                            {overallPct.toFixed(1)}%
                                        </span>
                                    </div>
                                    <div className="h-2 bg-slate-200 rounded overflow-hidden">
                                        <div
                                            className="h-full bg-[#00A3FF] transition-all"
                                            style={{ width: `${overallPct}%` }}
                                            data-testid="upload-overall-bar"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between text-[11px] text-slate-600 mt-1.5 tabular-nums">
                                        <span data-testid="upload-bytes">
                                            {fmtBytes(uploading.loadedBytes)} / {fmtBytes(uploading.totalBytes)}
                                        </span>
                                        <span data-testid="upload-eta">
                                            {speed > 0 ? `${fmtBytes(speed)}/s · ` : ""}{fmtEta(eta)} left
                                        </span>
                                    </div>
                                </>
                            )}

                            <div className={`${knownBytes ? "mt-4 pt-3 border-t border-slate-200" : "mt-3"}`}>
                                <div className="flex items-center gap-2 text-xs text-slate-700 mb-1">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00A3FF] shrink-0" />
                                    <span className="truncate flex-1" data-testid="upload-current-name">
                                        {uploading.currentName}
                                    </span>
                                    {knownBytes && (
                                        <span className="text-slate-500 tabular-nums text-[11px] shrink-0">
                                            {currentPct.toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                                {knownBytes && (
                                    <div className="h-1 bg-slate-100 rounded overflow-hidden">
                                        <div
                                            className="h-full bg-[#00A3FF]/70 transition-all"
                                            style={{ width: `${currentPct}%` }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 pt-3 border-t border-slate-200 space-y-2">
                                {uploading.errors && uploading.errors.length > 0 && (
                                    <div className="rounded border border-amber-200 bg-amber-50 p-2" data-testid="upload-failed-list">
                                        <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1">
                                            {uploading.errors.length} failed
                                        </div>
                                        <ul className="text-[11px] text-amber-900 space-y-0.5 max-h-24 overflow-y-auto">
                                            {uploading.errors.slice(0, 5).map((e, i) => (
                                                <li key={i} className="truncate" title={e.reason}>
                                                    • {e.file.name}
                                                </li>
                                            ))}
                                            {uploading.errors.length > 5 && (
                                                <li className="italic text-amber-700">…and {uploading.errors.length - 5} more</li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                                <div className="flex justify-end gap-2 flex-wrap">
                                    {uploading.finished ? (
                                        <>
                                            <button
                                                onClick={() => setUploading(null)}
                                                className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                                                data-testid="upload-close-btn"
                                            >
                                                Close
                                            </button>
                                            {uploading.errors && uploading.errors.length > 0 && (
                                                <button
                                                    onClick={retryFailed}
                                                    className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-[#007AFF] hover:bg-[#0369A1] text-white"
                                                    data-testid="upload-retry-btn"
                                                >
                                                    Retry {uploading.errors.length} failed
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                onClick={togglePause}
                                                disabled={uploading.canceled}
                                                className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid="upload-pause-btn"
                                            >
                                                {uploading.paused ? "Resume" : "Pause"}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (uploadAbortRef.current) {
                                                        uploadAbortRef.current.abort();
                                                        setUploading((prev) => prev ? { ...prev, canceled: true } : prev);
                                                    }
                                                    // If paused, wake the resolver so the loop can exit.
                                                    if (pauseRef.current.resolver) {
                                                        pauseRef.current.resolver();
                                                        pauseRef.current.resolver = null;
                                                    }
                                                }}
                                                disabled={uploading.canceled}
                                                className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid="upload-cancel-btn"
                                            >
                                                {uploading.canceled ? "Canceling…" : "Cancel upload"}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {treeMenu && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setTreeMenu(null)}
                        onContextMenu={(e) => { e.preventDefault(); setTreeMenu(null); }}
                        data-testid="files-tree-menu-backdrop"
                    />
                    <div
                        className="fixed z-50 bg-white border border-slate-200 rounded shadow-xl min-w-[180px] py-1 text-sm"
                        style={{ left: treeMenu.x, top: treeMenu.y }}
                        data-testid="files-tree-context-menu"
                    >
                        <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 truncate border-b border-slate-200 mb-1">
                            {treeMenu.folder.name}
                        </div>
                        <button
                            onClick={async () => {
                                const f = treeMenu.folder;
                                setTreeMenu(null);
                                // eslint-disable-next-line no-alert
                                const name = window.prompt("New subfolder name?");
                                if (!name || !name.trim()) return;
                                try {
                                    await createFolder(name.trim(), f.file_id);
                                    setExpandedIds((prev) => new Set(prev).add(f.file_id));
                                    refresh();
                                    toast.success(`Created "${name.trim()}" inside ${f.name}`);
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                }
                            }}
                            className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-slate-700 flex items-center gap-2"
                            data-testid="tree-ctx-new-subfolder"
                        >
                            <FolderPlus className="w-3.5 h-3.5 text-[#00A3FF]" /> New subfolder
                        </button>
                        {!treeMenu.folder.system && (
                            <>
                                <button
                                    onClick={() => {
                                        setRenaming(treeMenu.folder);
                                        setRenameName(treeMenu.folder.name);
                                        setTreeMenu(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-slate-700 flex items-center gap-2"
                                    data-testid="tree-ctx-rename"
                                >
                                    <FileText className="w-3.5 h-3.5 text-slate-500" /> Rename
                                </button>
                                <div className="border-t border-slate-200 my-1" />
                                <button
                                    onClick={async () => {
                                        const f = treeMenu.folder;
                                        setTreeMenu(null);
                                        // eslint-disable-next-line no-alert
                                        if (!window.confirm(`Delete "${f.name}" and everything inside?`)) return;
                                        try {
                                            await deleteFile(f.file_id);
                                            if (parent === f.file_id) setParent("root");
                                            refresh();
                                            toast.success(`Deleted "${f.name}"`);
                                        } catch (err) {
                                            toast.error(formatApiError(err));
                                        }
                                    }}
                                    className="w-full text-left px-3 py-1.5 hover:bg-rose-50 text-rose-600 flex items-center gap-2"
                                    data-testid="tree-ctx-delete"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                            </>
                        )}
                        {treeMenu.folder.system && isSuperadmin && (
                            <>
                                <button
                                    onClick={() => {
                                        setRenaming(treeMenu.folder);
                                        setRenameName(treeMenu.folder.name);
                                        setTreeMenu(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 hover:bg-amber-50 text-amber-700 flex items-center gap-2"
                                    data-testid="tree-ctx-rename-system"
                                >
                                    <FileText className="w-3.5 h-3.5 text-amber-500" />
                                    Rename pinned folder
                                </button>
                                <button
                                    onClick={() => {
                                        setIconPicker(treeMenu.folder);
                                        setTreeMenu(null);
                                    }}
                                    className="w-full text-left px-3 py-1.5 hover:bg-amber-50 text-amber-700 flex items-center gap-2"
                                    data-testid="tree-ctx-change-icon"
                                >
                                    <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                                    Change icon
                                </button>
                                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-amber-600 italic">
                                    Superadmin only
                                </div>
                            </>
                        )}
                        {treeMenu.folder.system && !isSuperadmin && (
                            <div className="px-3 py-1.5 text-[11px] text-slate-500 italic">
                                System folder — rename & delete are locked.
                            </div>
                        )}
                    </div>
                </>
            )}

            {selected.size > 0 && !isTrashView && (
                <div
                    className="fixed bottom-20 md:bottom-24 left-1/2 -translate-x-1/2 z-[60] bg-slate-900 text-white rounded-full shadow-2xl border border-slate-700 flex items-center gap-2 pl-4 pr-2 py-1.5"
                    data-testid="files-bulk-bar"
                >
                    <span className="text-xs font-bold uppercase tracking-wider">
                        {selected.size} selected
                    </span>
                    <button
                        onClick={() => setSelected(new Set())}
                        className="text-[11px] uppercase tracking-wider px-2 py-1 rounded hover:bg-white/10"
                        data-testid="files-bulk-clear"
                    >
                        Clear
                    </button>
                    <button
                        onClick={async () => {
                            try {
                                const blob = await bulkDownloadFiles(Array.from(selected));
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = "steeltooth-files.zip";
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                setTimeout(() => URL.revokeObjectURL(url), 60_000);
                                toast.success(`Downloaded ${selected.size} item${selected.size === 1 ? "" : "s"}`);
                            } catch (err) {
                                toast.error(formatApiError(err) || "Download failed.");
                            }
                        }}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full bg-[#00A3FF] hover:bg-[#0369A1] text-white"
                        data-testid="files-bulk-download"
                    >
                        <Download className="w-3.5 h-3.5" /> Download
                    </button>
                    <button
                        onClick={async () => {
                            // eslint-disable-next-line no-alert
                            if (!window.confirm(`Delete ${selected.size} selected item(s) and their contents? This can't be undone.`)) return;
                            const ids = Array.from(selected);
                            try {
                                const r = await bulkDeleteFiles(ids);
                                setSelected(new Set());
                                refresh();
                                // iter-163 — Undo toast. Sonner supports actions.
                                toast.success(`Deleted ${r.deleted} item${r.deleted === 1 ? "" : "s"}`, {
                                    duration: 5000,
                                    action: (r.deleted_ids && r.deleted_ids.length) ? {
                                        label: "Undo",
                                        onClick: async () => {
                                            try {
                                                const rr = await bulkRestoreFiles(r.deleted_ids);
                                                refresh();
                                                toast.success(`Restored ${rr.restored} item${rr.restored === 1 ? "" : "s"}`);
                                            } catch (err) {
                                                toast.error(formatApiError(err) || "Restore failed.");
                                            }
                                        },
                                    } : undefined,
                                });
                                if (r.skipped && r.skipped.length) {
                                    toast.warning(`Skipped ${r.skipped.length} system folder${r.skipped.length === 1 ? "" : "s"}`);
                                }
                            } catch (err) {
                                toast.error(formatApiError(err) || "Delete failed.");
                            }
                        }}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white"
                        data-testid="files-bulk-delete"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                </div>
            )}

            {selected.size > 0 && isTrashView && (
                <div
                    className="fixed bottom-20 md:bottom-24 left-1/2 -translate-x-1/2 z-[60] bg-slate-900 text-white rounded-full shadow-2xl border border-slate-700 flex items-center gap-2 pl-4 pr-2 py-1.5"
                    data-testid="files-trash-bar"
                >
                    <span className="text-xs font-bold uppercase tracking-wider">
                        {selected.size} selected
                    </span>
                    <button
                        onClick={() => setSelected(new Set())}
                        className="text-[11px] uppercase tracking-wider px-2 py-1 rounded hover:bg-white/10"
                        data-testid="files-trash-clear"
                    >
                        Clear
                    </button>
                    <button
                        onClick={async () => {
                            const ids = Array.from(selected);
                            try {
                                const rr = await bulkRestoreFiles(ids);
                                setSelected(new Set());
                                refresh();
                                toast.success(`Restored ${rr.restored} item${rr.restored === 1 ? "" : "s"}`);
                            } catch (err) {
                                toast.error(formatApiError(err) || "Restore failed.");
                            }
                        }}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid="files-trash-restore"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                    <button
                        onClick={async () => {
                            // eslint-disable-next-line no-alert
                            if (!window.confirm(`Permanently delete ${selected.size} item(s)? This CANNOT be undone.`)) return;
                            const ids = Array.from(selected);
                            try {
                                const r = await bulkPermanentDeleteFiles(ids);
                                setSelected(new Set());
                                refresh();
                                if (r.deleted === 0) {
                                    toast.warning("Nothing was deleted — those items aren't in Recently deleted anymore.");
                                } else {
                                    toast.success(`Permanently deleted ${r.deleted} item${r.deleted === 1 ? "" : "s"}`);
                                }
                            } catch (err) {
                                toast.error(formatApiError(err) || "Permanent delete failed.");
                            }
                        }}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white"
                        data-testid="files-trash-purge"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Delete forever
                    </button>
                </div>
            )}

            {iconPicker && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={() => setIconPicker(null)}
                    data-testid="icon-picker-dialog"
                >
                    <div
                        className="bg-white border border-slate-200 rounded-lg shadow-2xl max-w-md w-full p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h3 className="font-bold text-slate-900 text-sm">Choose an icon</h3>
                                <div className="text-[11px] text-slate-500 truncate">
                                    for "{iconPicker.name}"
                                </div>
                            </div>
                            <button
                                onClick={() => setIconPicker(null)}
                                className="p-1 rounded hover:bg-slate-100 text-slate-500"
                                data-testid="icon-picker-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {iconPicker.custom_icon && (
                                <button
                                    onClick={async () => {
                                        try {
                                            await clearFolderIconSvg(iconPicker.file_id);
                                            setIconPicker(null);
                                            refresh();
                                            toast.success("Custom icon removed.");
                                        } catch (err) {
                                            toast.error(formatApiError(err));
                                        }
                                    }}
                                    data-testid="icon-opt-clear-custom"
                                    className="aspect-square flex flex-col items-center justify-center gap-0.5 rounded border border-rose-200 bg-rose-50 hover:bg-rose-100 shadow-sm transition-all col-span-2"
                                >
                                    <img src={iconPicker.custom_icon} alt="" className="w-6 h-6 object-contain" />
                                    <span className="text-[9px] uppercase tracking-wider text-rose-700 truncate max-w-full">Remove custom</span>
                                </button>
                            )}
                            <label
                                data-testid="icon-opt-upload-svg"
                                className="aspect-square flex flex-col items-center justify-center gap-0.5 rounded border border-dashed border-[#00A3FF] bg-[#00A3FF]/5 hover:bg-[#00A3FF]/10 shadow-sm transition-all cursor-pointer col-span-2"
                            >
                                <Upload className="w-6 h-6 text-[#00A3FF]" />
                                <span className="text-[9px] uppercase tracking-wider text-[#00A3FF] truncate max-w-full">Upload SVG</span>
                                <input
                                    type="file"
                                    accept=".svg,image/svg+xml"
                                    className="hidden"
                                    onChange={async (e) => {
                                        const f = e.target.files?.[0];
                                        if (!f) return;
                                        try {
                                            await uploadFolderIconSvg(iconPicker.file_id, f);
                                            setIconPicker(null);
                                            refresh();
                                            toast.success(`Custom icon set for ${iconPicker.name}`);
                                        } catch (err) {
                                            toast.error(formatApiError(err));
                                        } finally {
                                            e.target.value = "";
                                        }
                                    }}
                                />
                            </label>
                            {Object.entries(FOLDER_ICON_OPTIONS).map(([key, Icon]) => {
                                const active = !iconPicker.custom_icon && (iconPicker.icon || "folder") === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={async () => {
                                            try {
                                                await updateFileIcon(iconPicker.file_id, key);
                                                setIconPicker(null);
                                                refresh();
                                                toast.success(`Icon updated for ${iconPicker.name}`);
                                            } catch (err) {
                                                toast.error(formatApiError(err));
                                            }
                                        }}
                                        data-testid={`icon-opt-${key}`}
                                        className={`aspect-square flex flex-col items-center justify-center gap-0.5 rounded border shadow-sm transition-all ${active ? "border-[#00A3FF] bg-[#00A3FF]/10" : "border-slate-200 bg-white hover:border-[#00A3FF]"}`}
                                    >
                                        <Icon className="w-6 h-6 text-[#00A3FF]" />
                                        <span className="text-[9px] uppercase tracking-wider text-slate-500 truncate max-w-full">{key}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {iconLibrary.length > 0 && (
                            <div className="mt-4 pt-3 border-t border-slate-200">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                                    Recently uploaded ({iconLibrary.length})
                                </div>
                                <div className="grid grid-cols-6 gap-2" data-testid="icon-library-grid">
                                    {iconLibrary.map((url, i) => (
                                        <button
                                            key={i}
                                            onClick={async () => {
                                                try {
                                                    await applyFolderIconFromLibrary(iconPicker.file_id, url);
                                                    setIconPicker(null);
                                                    refresh();
                                                    toast.success(`Icon applied to ${iconPicker.name}`);
                                                } catch (err) {
                                                    toast.error(formatApiError(err));
                                                }
                                            }}
                                            data-testid={`icon-library-${i}`}
                                            className={`aspect-square flex items-center justify-center rounded border shadow-sm transition-all ${iconPicker.custom_icon === url ? "border-[#00A3FF] bg-[#00A3FF]/10" : "border-slate-200 bg-white hover:border-[#00A3FF]"}`}
                                        >
                                            <img src={url} alt="" className="w-6 h-6 object-contain" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {renaming && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="files-rename-dialog">
                    <div className="bg-white border border-slate-200 rounded p-4 max-w-md w-full mx-4">
                        <h3 className="text-slate-900 font-bold mb-3">Rename</h3>
                        <Input
                            value={renameName}
                            onChange={(e) => setRenameName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleRename()}
                            autoFocus
                            className="bg-slate-100 border-slate-200 text-slate-900"
                            data-testid="files-rename-input"
                        />
                        <div className="flex justify-end gap-2 mt-3">
                            <Button variant="outline" size="sm" onClick={() => setRenaming(null)} className="border-slate-200 text-slate-900">Cancel</Button>
                            <Button size="sm" onClick={handleRename} className="bg-[#007AFF] hover:bg-[#0369A1] text-white" data-testid="files-rename-save">Save</Button>
                        </div>
                    </div>
                </div>
            )}

            {preview && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreview(null)} data-testid="files-preview-dialog">
                    <div className="bg-white border border-slate-200 rounded max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-3 border-b border-slate-200">
                            <div className="text-slate-900 font-medium truncate">{preview.item.name}</div>
                            <div className="flex items-center gap-1 relative">
                                <button
                                    onClick={() => setShareOpen((v) => !v)}
                                    className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                                    data-testid="files-preview-share"
                                    title="Share"
                                >
                                    <Share2 className="w-4 h-4" />
                                </button>
                                {shareOpen && (
                                    <SharePopover item={preview.item} onClose={() => setShareOpen(false)} />
                                )}
                                <a href={downloadFileUrl(preview.item.file_id)} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Download" data-testid="files-preview-download"><Download className="w-4 h-4" /></a>
                                <button onClick={() => setPreview(null)} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" data-testid="files-preview-close"><X className="w-4 h-4" /></button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto p-3">
                            <PreviewBody preview={preview} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// iter-166 — Auth-aware image thumbnail. The download endpoint needs a
// Bearer token in the Authorization header, which <img src> requests
// can't attach. We fetch the blob through the axios client (which has
// the token interceptor), create a stable object URL, cache it by
// file_id so repeat renders don't re-download, and hand it to a plain
// <img>. Falls back to a plain icon on failure so a single flaky file
// doesn't leave a broken image icon.
const __thumbCache = new Map();
function AuthImage({ fileId, alt, className, fallback: Fallback = null }) {
    const [url, setUrl] = React.useState(() => __thumbCache.get(fileId) || null);
    const [errored, setErrored] = React.useState(false);
    React.useEffect(() => {
        if (__thumbCache.has(fileId)) {
            setUrl(__thumbCache.get(fileId));
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                // iter-167 — ?render=web asks the backend to transcode
                // HEIC/HEIF into browser-safe JPEG. Other formats pass
                // through unchanged.
                const res = await api.get(`/files/${fileId}/download`, {
                    params: { render: "web" },
                    responseType: "blob",
                });
                if (cancelled) return;
                const objectUrl = URL.createObjectURL(res.data);
                __thumbCache.set(fileId, objectUrl);
                setUrl(objectUrl);
            } catch {
                if (!cancelled) setErrored(true);
            }
        })();
        return () => { cancelled = true; };
    }, [fileId]);
    if (errored && Fallback) return Fallback;
    if (!url) {
        return <div className={`${className || ""} bg-slate-100 animate-pulse`} aria-hidden="true" />;
    }
    return <img src={url} alt={alt} loading="lazy" className={className} />;
}

// iter-166 — Preview body renderer. Uses fetch() with the auth
// interceptor so the browser sends the Bearer token, then hands the
// blob to <img>/<iframe>/<video>/<audio> as an object URL. Fixes the
// "Not authenticated" screen the user was seeing on PNG + PDF preview.
function PreviewBody({ preview }) {
    const { item, info } = preview;
    const [objectUrl, setObjectUrl] = React.useState(null);
    const [loadErr, setLoadErr] = React.useState(null);
    const needsBlob = ["image", "pdf", "video", "audio"].includes(info.type);

    React.useEffect(() => {
        if (!needsBlob) return undefined;
        let revoked = false;
        let currentUrl = null;
        (async () => {
            try {
                // iter-167 — send ?render=web so HEIC/HEIF images come
                // back as JPEG. No-op for browser-safe formats.
                const res = await api.get(`/files/${item.file_id}/download`, {
                    params: info.type === "image" ? { render: "web" } : undefined,
                    responseType: "blob",
                });
                if (revoked) return;
                currentUrl = URL.createObjectURL(res.data);
                setObjectUrl(currentUrl);
            } catch (err) {
                if (!revoked) setLoadErr(formatApiError(err) || "Preview failed.");
            }
        })();
        return () => {
            revoked = true;
            if (currentUrl) URL.revokeObjectURL(currentUrl);
        };
    }, [item.file_id, needsBlob]);

    if (needsBlob && loadErr) {
        return <div className="p-6 text-sm text-rose-600" data-testid="files-preview-error">{loadErr}</div>;
    }
    if (needsBlob && !objectUrl) {
        return (
            <div className="p-10 flex items-center justify-center text-slate-500 text-sm" data-testid="files-preview-loading">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading preview…
            </div>
        );
    }

    if (info.type === "image") {
        return <img src={objectUrl} alt={item.name} className="max-w-full mx-auto" data-testid="files-preview-image" />;
    }
    if (info.type === "pdf") {
        // iter-167 — Android Chrome refuses to render blob-url PDFs
        // inside an iframe (shows the "Open" placeholder). Detect
        // mobile UA and swap to a big "Open PDF" button that opens the
        // token-authenticated URL in a new tab. Desktop keeps the
        // inline iframe experience.
        const isMobile = typeof navigator !== "undefined" &&
            /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
        if (isMobile) {
            return (
                <div className="p-8 flex flex-col items-center gap-4" data-testid="files-preview-pdf-mobile">
                    <div className="w-24 h-24 rounded-xl bg-rose-100 flex items-center justify-center">
                        <FileText className="w-10 h-10 text-rose-600" />
                    </div>
                    <div className="text-sm text-slate-700 truncate max-w-full text-center">{item.name}</div>
                    <div className="text-xs text-slate-500 max-w-xs text-center">
                        PDFs preview inline on desktop. Tap below to open on this device.
                    </div>
                    <a
                        href={downloadFileUrl(item.file_id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded bg-[#007AFF] hover:bg-[#0369A1] text-white text-sm font-bold"
                        data-testid="files-preview-pdf-open"
                    >
                        <FileText className="w-4 h-4" /> Open PDF
                    </a>
                </div>
            );
        }
        return <iframe src={objectUrl} title={item.name} className="w-full h-[75vh] bg-white" data-testid="files-preview-pdf" />;
    }
    if (info.type === "video") {
        return (
            <video src={objectUrl} controls preload="metadata" className="max-w-full max-h-[75vh] mx-auto bg-black rounded" data-testid="files-preview-video" />
        );
    }
    if (info.type === "audio") {
        return (
            <div className="p-8 flex flex-col items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-[#00A3FF]/10 flex items-center justify-center">
                    <FileText className="w-10 h-10 text-[#00A3FF]" />
                </div>
                <div className="text-sm text-slate-700 truncate max-w-full">{item.name}</div>
                <audio src={objectUrl} controls preload="metadata" className="w-full max-w-md" data-testid="files-preview-audio" />
            </div>
        );
    }
    if (info.type === "text") {
        return <pre className="text-xs text-slate-700 whitespace-pre-wrap break-all">{info.text}</pre>;
    }
    if (info.type === "docx") {
        return (
            <div
                className="docx-preview prose prose-slate max-w-none text-slate-900 text-sm"
                dangerouslySetInnerHTML={{ __html: info.html || "<p><em>Empty document.</em></p>" }}
                data-testid="files-preview-docx"
            />
        );
    }
    if (info.type === "sheet" && info.sheets?.length) {
        return <SheetTabs sheets={info.sheets} />;
    }
    return (
        <div className="p-8 flex flex-col items-center text-center gap-3" data-testid="files-preview-unsupported">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center">
                <FileText className="w-8 h-8 text-slate-500" />
            </div>
            <div className="text-slate-900 font-medium truncate max-w-full">{item.name}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">
                {(info.mime || item.mime_type || "Unknown type")}
                {info.size ? ` · ${fmtBytes(info.size)}` : ""}
            </div>
            <div className="text-sm text-slate-600 max-w-md">
                {info.message || "This file type can't be previewed inside the app — open it in another app or share it."}
            </div>
            <a
                href={downloadFileUrl(item.file_id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-[#007AFF] hover:bg-[#0369A1] text-white text-sm font-bold"
            >
                <Download className="w-4 h-4" /> Download
            </a>
        </div>
    );
}

function SheetTabs({ sheets }) {
    const [active, setActive] = React.useState(0);
    const s = sheets[active] || sheets[0];
    return (
        <div className="flex flex-col h-full" data-testid="files-preview-sheet">
            {sheets.length > 1 && (
                <div className="flex items-center gap-1 mb-2 overflow-x-auto">
                    {sheets.map((sh, i) => (
                        <button
                            key={i}
                            onClick={() => setActive(i)}
                            data-testid={`files-preview-sheet-tab-${i}`}
                            className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded shrink-0 ${
                                i === active
                                    ? "bg-[#00A3FF] text-white"
                                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                        >
                            {sh.name}
                        </button>
                    ))}
                </div>
            )}
            <div className="overflow-auto">
                <table className="w-full text-xs text-slate-900 border-collapse">
                    <tbody>
                        {(s?.rows || []).slice(0, 500).map((r, ri) => (
                            <tr key={ri} className="border-b border-slate-200/60">
                                {r.map((c, ci) => (
                                    <td key={ci} className="px-2 py-1 border-l border-slate-200/40 first:border-l-0 whitespace-nowrap">{c}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// iter-151 — share menu that appears next to Download in the preview.
// Native `navigator.share` on mobile → OS share sheet (SMS, Email, all
// installed apps). Desktop fallback: mailto: + sms: URIs + Copy Link.
function SharePopover({ item, onClose }) {
    const shareUrl = `${window.location.origin}/api/files/${item.file_id}/download`;
    const canNative = typeof navigator !== "undefined" && !!navigator.share;

    const doNative = async () => {
        try {
            // Try attaching the actual file (best on iOS/Android) so the
            // recipient doesn't need to be signed in to view it.
            try {
                const resp = await fetch(`/api/files/${item.file_id}/download`, { credentials: "include" });
                if (resp.ok) {
                    const blob = await resp.blob();
                    const f = new File([blob], item.name, { type: blob.type || item.mime_type || "application/octet-stream" });
                    if (navigator.canShare && navigator.canShare({ files: [f] })) {
                        await navigator.share({ files: [f], title: item.name, text: item.name });
                        onClose();
                        return;
                    }
                }
            } catch { /* fall through to URL share */ }
            await navigator.share({ title: item.name, text: `File: ${item.name}`, url: shareUrl });
            onClose();
        } catch (err) {
            if (err?.name !== "AbortError") toast.error("Share was canceled or failed.");
        }
    };

    const doMail = () => {
        const subj = encodeURIComponent(`File: ${item.name}`);
        const body = encodeURIComponent(
            `I want to share "${item.name}" with you.\n\nView it here:\n${shareUrl}\n\n(A Steeltooth login is required to view.)`,
        );
        window.location.href = `mailto:?subject=${subj}&body=${body}`;
        onClose();
    };

    const doSms = () => {
        const body = encodeURIComponent(
            `Check out "${item.name}":\n${shareUrl}\n(Steeltooth login required)`,
        );
        window.location.href = `sms:?&body=${body}`;
        onClose();
    };

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied to clipboard.");
        } catch {
            toast.error("Couldn't copy link.");
        }
        onClose();
    };

    return (
        <>
            <div className="fixed inset-0 z-40" onClick={onClose} />
            <div
                className="absolute top-full right-0 mt-1 z-50 bg-white border border-slate-200 rounded shadow-xl min-w-[220px] py-1 text-sm"
                data-testid="files-share-menu"
            >
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 border-b border-slate-200 mb-1">
                    Share this file
                </div>
                {canNative && (
                    <button
                        onClick={doNative}
                        className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-slate-700 flex items-center gap-2"
                        data-testid="share-native"
                    >
                        <Share2 className="w-4 h-4 text-[#00A3FF]" /> Share via device…
                    </button>
                )}
                <button
                    onClick={doSms}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-slate-700 flex items-center gap-2"
                    data-testid="share-sms"
                >
                    <MessageSquare className="w-4 h-4 text-emerald-500" /> Text message
                </button>
                <button
                    onClick={doMail}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-slate-700 flex items-center gap-2"
                    data-testid="share-email"
                >
                    <Mail className="w-4 h-4 text-amber-500" /> Email
                </button>
                <div className="border-t border-slate-200 my-1" />
                <button
                    onClick={doCopy}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-100 text-slate-700 flex items-center gap-2"
                    data-testid="share-copy"
                >
                    <LinkIcon className="w-4 h-4 text-slate-500" /> Copy link
                </button>
            </div>
        </>
    );
}
